/**
 * Adds a descriptive `title` to any `<iframe>` in an HTML string that has no
 * meaningful title, so screen readers announce it (WCAG 1.3.1 Info &
 * Relationships / 4.1.2 Name, Role, Value). An empty `title=""` is treated as
 * missing (it still fails `iframe-has-title` / naming expectations).
 *
 * Iframes that arrive inside hotelier-authored HTML (room descriptions rendered
 * via `dangerouslySetInnerHTML`) bypass the `jsx-a11y/iframe-has-title` lint,
 * so they are titled here at render time instead.
 *
 * When the HTML contains no iframe (the overwhelming majority of descriptions)
 * the original string is returned untouched — no parsing, sanitizing, or
 * re-serialization. Only when an untitled iframe is present is the body parsed
 * and re-serialized via the browser HTML parser; that path adds the title
 * attribute and does not sanitize or strip, but re-serialization can normalize
 * incidental markup (attribute-quote style, entity references, void elements),
 * so it is intentionally scoped to the rare iframe case.
 */
/**
 * Neutralizes focusable descendants in a truncated preview of hotelier HTML.
 *
 * Room-card descriptions are shown with a CSS line-clamp (`-webkit-line-clamp`
 * + `overflow: hidden`): the overflow is hidden visually but every node stays
 * in the DOM, the accessibility tree and the tab order — so a keyboard user can
 * Tab onto a link or iframe that is completely clipped (WCAG 2.4.3 Focus Order
 * / 2.4.11 Focus Not Obscured — BENG-4058 #56 / BENG-4060 #60). The full,
 * interactive description remains available via the "View details" modal, so
 * the clamped preview is made non-interactive: links become plain text (their
 * `href` is removed), embedded iframes are dropped, and any explicit `tabindex`
 * is set to `-1`.
 *
 * When there is nothing focusable to neutralize (the common case) the original
 * string is returned untouched — no parsing or re-serialization.
 */
export const neutralizeInteractiveHtml = (html) => {
    if (!/<a[\s>]|<iframe|tabindex/i.test(html)) {
        return html;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.body
        .querySelectorAll('a[href]')
        .forEach((anchor) => anchor.removeAttribute('href'));
    doc.body.querySelectorAll('iframe').forEach((iframe) => iframe.remove());
    doc.body
        .querySelectorAll('[tabindex]')
        .forEach((el) => el.setAttribute('tabindex', '-1'));
    return doc.body.innerHTML;
};
export const addIframeTitles = (html, title) => {
    // Case-insensitive so uppercase/mixed-case tags from older CMSes or manual
    // hotelier HTML (`<IFRAME>`, `<IFrame>`) still take the parsing path.
    if (!/<iframe/i.test(html)) {
        return html;
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const untitledIframes = Array.from(doc.body.querySelectorAll('iframe')).filter((iframe) => { var _a; return !((_a = iframe.getAttribute('title')) === null || _a === void 0 ? void 0 : _a.trim()); });
    if (untitledIframes.length === 0) {
        return html;
    }
    untitledIframes.forEach((iframe) => {
        iframe.setAttribute('title', title);
    });
    return doc.body.innerHTML;
};
